import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  adminApi,
  type PlatformUserRow,
} from '@/api/admin';
import {
  platformAdminRbacApi,
  type PlatformRoleRow,
} from '@/api/platform-admin-rbac';
import { refreshSessionFromServer } from '@/lib/session-sync';
import { useAuthStore } from '@/store';
import { toast } from 'sonner';
import { btnOutlineNeon, btnPrimaryNeon } from '@/lib/admin-ui';
import { usePlatformPermission } from '@/hooks/use-platform-permission';

const STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;

const PLATFORM_ROLE_ADMIN_ID = 'role-admin';

export function PlatformUsersPage() {
  const canManagePlatformUsers = usePlatformPermission(
    'platform:platform_user:manage',
  );
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [rows, setRows] = useState<PlatformUserRow[]>([]);
  const [platformRoles, setPlatformRoles] = useState<PlatformRoleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [createRoleId, setCreateRoleId] = useState(PLATFORM_ROLE_ADMIN_ID);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  async function load() {
    try {
      setLoading(true);
      const [users, rolesRes] = await Promise.all([
        adminApi.getPlatformUsers(),
        platformAdminRbacApi.listRoles(),
      ]);
      setRows(users);
      setPlatformRoles(rolesRes.data);
    } catch {
      toast.error('加载平台用户失败');
      setRows([]);
      setPlatformRoles([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      setCreating(true);
      await adminApi.createPlatformUser({
        email: email.trim(),
        password,
        name: name.trim() || undefined,
        roleId: createRoleId,
      });
      toast.success('已创建平台用户');
      setEmail('');
      setPassword('');
      setName('');
      setCreateRoleId(PLATFORM_ROLE_ADMIN_ID);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '创建失败';
      toast.error(typeof msg === 'string' ? msg : '创建失败');
    } finally {
      setCreating(false);
    }
  }

  async function updateStatus(id: string, status: string) {
    try {
      await adminApi.updatePlatformUser(id, { status });
      if (id === currentUserId) await refreshSessionFromServer();
      toast.success('状态已更新');
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '更新失败';
      toast.error(typeof msg === 'string' ? msg : '更新失败');
    }
  }

  async function updatePlatformRole(userId: string, roleId: string) {
    try {
      await adminApi.setPlatformUserRole(userId, roleId);
      if (userId === currentUserId) await refreshSessionFromServer();
      toast.success(
        userId === currentUserId
          ? '平台角色已更新'
          : '平台角色已更新，对方账号需刷新或重新登录后生效',
      );
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '更新失败';
      toast.error(typeof msg === 'string' ? msg : '更新失败');
    }
  }

  async function confirmDelete() {
    if (!deleteId) return;
    try {
      await adminApi.deletePlatformUser(deleteId);
      toast.success('已删除');
      setDeleteId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '删除失败';
      toast.error(typeof msg === 'string' ? msg : '删除失败');
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">平台用户</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          管理可登录运营后台的账号；受保护账号不可删除。
        </p>
      </div>

      {canManagePlatformUsers && (
      <Card className="border-white/10 bg-zinc-900/40">
        <CardHeader>
          <CardTitle className="text-lg">新建平台用户</CardTitle>
          <CardDescription>
            选择平台角色（定义见「平台角色」）；默认超级管理员与演示种子一致。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleCreate}
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5"
          >
            <div className="space-y-2">
              <Label htmlFor="pf-email">邮箱</Label>
              <Input
                id="pf-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-white/10 bg-zinc-950/60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-pass">初始密码</Label>
              <Input
                id="pf-pass"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/10 bg-zinc-950/60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-name">姓名（可选）</Label>
              <Input
                id="pf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="border-white/10 bg-zinc-950/60"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pf-role">平台角色</Label>
              <Select value={createRoleId} onValueChange={setCreateRoleId}>
                <SelectTrigger
                  id="pf-role"
                  className="h-10 border-white/10 bg-zinc-950/60"
                >
                  <SelectValue placeholder="选择角色" />
                </SelectTrigger>
                <SelectContent>
                  {platformRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={creating} className={btnPrimaryNeon}>
                {creating ? '提交中…' : '创建'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      )}

      <Card className="border-white/10 bg-zinc-900/40">
        <CardHeader>
          <CardTitle className="text-lg">用户列表</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead>邮箱</TableHead>
                  <TableHead>姓名</TableHead>
                  <TableHead>平台角色</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>保护</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const currentRoleId = row.roles?.[0]?.role.id ?? '';
                  const disableRoleEdit =
                    row.id === currentUserId ||
                    !canManagePlatformUsers ||
                    row.isProtected;
                  return (
                  <TableRow key={row.id} className="border-white/10">
                    <TableCell className="font-medium">{row.email}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      <Select
                        value={
                          currentRoleId &&
                          platformRoles.some((r) => r.id === currentRoleId)
                            ? currentRoleId
                            : undefined
                        }
                        disabled={disableRoleEdit || platformRoles.length === 0}
                        onValueChange={(v) => updatePlatformRole(row.id, v)}
                      >
                        <SelectTrigger
                          className="h-8 min-w-[160px] border-white/10 bg-zinc-950/60"
                          title={
                            row.isProtected
                              ? '受保护账号不可更换平台角色'
                              : row.id === currentUserId
                                ? '不能修改自己的平台角色'
                                : undefined
                          }
                        >
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {platformRoles.map((r) => (
                            <SelectItem key={r.id} value={r.id}>
                              {r.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={row.status}
                        disabled={!canManagePlatformUsers || row.isProtected}
                        onValueChange={(v) => updateStatus(row.id, v)}
                      >
                        <SelectTrigger
                          className="h-8 w-[140px] border-white/10 bg-zinc-950/60"
                          title={
                            row.isProtected
                              ? '受保护账号须保持启用，不可禁用或停用'
                              : undefined
                          }
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {row.isProtected ? '是' : '否'}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManagePlatformUsers && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className={btnOutlineNeon}
                          disabled={row.isProtected}
                          title={
                            row.isProtected ? '受保护账号不可删除' : undefined
                          }
                          onClick={() => setDeleteId(row.id)}
                        >
                          删除
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="border-white/10 bg-zinc-950 text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除该平台用户？</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              此操作不可恢复。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={btnOutlineNeon}>取消</AlertDialogCancel>
            <AlertDialogAction
              className={btnPrimaryNeon}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
